import { useCallback, useEffect, useState } from "react";
import { ArrowRight, Loader2, RefreshCw, Settings2 } from "lucide-react";
import { api } from "./lib/api.js";
import { C, fontStack, inputStyle } from "./theme.js";

/* ============================================================================
   מסך בעל הפלטפורמה.

   בנוי לטלפון קודם: כרטיסים ולא טבלאות. הנתונים כאן הם שבעה שדות לכל
   חשבון, וטבלה כזו על מסך צר מחייבת גלילה לרוחב — כלומר בפועל לא נקראת.

   מה שהוא רואה: חשבונות, מצב מנוי, ומדדי שימוש. מה שהוא לא רואה: את
   הנתונים עצמם. אין מסלול שמחזיר מסמך של חשבון אחר.
   ============================================================================ */

const shekels = (agorot) =>
  "₪" + (Math.round(agorot || 0) / 100).toLocaleString("he-IL", {
    minimumFractionDigits: 0, maximumFractionDigits: 2,
  });

const heDate = (d) => (d ? new Date(d).toLocaleDateString("he-IL") : "—");

const daysLeft = (d) => (d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null);

/* מתי המשתמש נראה לאחרונה, בניסוח שאפשר לסרוק ברשימה ארוכה.
   "מחובר עכשיו" הוא פעילות בחמש הדקות האחרונות — לא סשן תקף: סשן חי
   30 יום, וחשבון שנפתח פעם אחת ונזנח נראה בו בדיוק כמו חשבון פעיל. */
function seenLabel(value) {
  if (!value) return { text: "טרם נכנס", tone: "idle" };
  const min = Math.floor((Date.now() - new Date(value).getTime()) / 60000);
  if (min < 5) return { text: "מחובר עכשיו", tone: "live" };
  if (min < 60) return { text: `לפני ${min} דק׳`, tone: "recent" };
  const hours = Math.floor(min / 60);
  if (hours < 24) return { text: `לפני ${hours} שע׳`, tone: "recent" };
  const days = Math.floor(hours / 24);
  if (days === 1) return { text: "אתמול", tone: "idle" };
  if (days < 30) return { text: `לפני ${days} ימים`, tone: "idle" };
  return { text: `לא נכנס ${Math.floor(days / 30)} חודשים`, tone: "cold" };
}

const SEEN_TONE = {
  live: { bg: "#E6F2EC", fg: "#2E7D62" },
  recent: { bg: "#EEF1EE", fg: "#5C6F73" },
  idle: { bg: "#EEF1EE", fg: "#5C6F73" },
  cold: { bg: "#FBF2E3", fg: "#6B4E12" },
};

const MODEL_LABEL = {
  flat: "מחיר קבוע",
  per_unit: "לפי יחידה",
  revenue_share: "אחוז מהגבייה",
};

const TABS = [
  ["accounts", "חשבונות"],
  ["settings", "ברירות מחדל"],
  ["feedback", "הצעות לשיפור"],
  ["payments", "תשלומים"],
  ["compare", "השוואה"],
  ["funnel", "משפך הרשמה"],
];

const PLAN_LABEL = { "1y": "מנוי לשנה", "2y": "מבצע שנתיים" };

export default function Admin({ onExit }) {
  const [tab, setTab] = useState("accounts");

  return (
    <div dir="rtl" className="min-h-screen w-full pb-20"
      style={{ background: C.surface, color: C.ink, fontFamily: fontStack }}>
      <div className="max-w-4xl mx-auto px-3 sm:px-6">
        <header className="pt-5 pb-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[11px] tracking-widest" style={{ color: C.ochre }}>
                ניהול הפלטפורמה
              </div>
              <h1 className="disp text-xl sm:text-2xl font-bold">
                חשבונות ומנויים
              </h1>
            </div>
            <button onClick={onExit}
              className="rounded-lg px-2.5 py-2 text-xs flex items-center gap-1 shrink-0"
              style={{ border: `1px solid ${C.line}`, color: C.muted }}>
              <ArrowRight size={14} /> לנכסים שלי
            </button>
          </div>
        </header>

        {/* גלילה אופקית רק לפסי הלשוניות, שהם קצרים ממילא */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className="rounded-lg px-3 py-1.5 text-sm whitespace-nowrap"
              style={tab === k
                ? { background: C.teal, color: "#fff" }
                : { border: `1px solid ${C.line}`, color: C.muted, background: C.card }}>
              {label}
            </button>
          ))}
        </div>

        {tab === "accounts" && <Accounts />}
        {tab === "settings" && <SettingsTab />}
        {tab === "feedback" && <Feedback />}
        {tab === "payments" && <Payments />}
        {tab === "compare" && <Compare />}
        {tab === "funnel" && <SignupFunnel />}
      </div>
    </div>
  );
}

/* --------------------------------------------------------- רכיבי עזר --- */

function Notice({ text, tone = "bad" }) {
  const s = tone === "bad"
    ? { background: "#FBEDE9", color: "#7A2E1E" }
    : { background: "#E6F2EC", color: C.paid };
  return <div className="rounded-lg p-2.5 text-sm mb-3" style={s}>{text}</div>;
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-[12px]" style={{ color: C.muted }}>{label}</span>
      {children}
      {hint && <span className="block text-[11px] mt-0.5" style={{ color: C.muted }}>{hint}</span>}
    </label>
  );
}

/* ========================================================== חשבונות === */

function Accounts() {
  const [accounts, setAccounts] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("new");
  const [limit, setLimit] = useState(50);

  const load = useCallback(async () => {
    try {
      setAccounts((await api.get("/billing/accounts")).accounts);
      setErr("");
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (err) return <Notice text={err} />;
  if (accounts === null) return <Loader2 className="animate-spin" size={20} />;

  const needle = q.trim().toLowerCase();
  const matched = needle
    ? accounts.filter((a) =>
      (a.name || "").toLowerCase().includes(needle) ||
      (a.owner_email || "").toLowerCase().includes(needle))
    : accounts;

  /* מיון ועימוד. עם אלף חשבונות הדף היה באורך 50,000 פיקסלים, וכל
     גלילה אליו רינדרה את כולם. ברירת המחדל היא "הצטרפו לאחרונה" —
     מי שנרשם עכשיו הוא מי שבודקים. */
  const sorted = [...matched].sort((a, b) => {
    if (sort === "name") return String(a.name || "").localeCompare(String(b.name || ""), "he");
    if (sort === "tenants") return (Number(b.tenants) || 0) - (Number(a.tenants) || 0);
    if (sort === "active") return String(b.last_seen_at || "").localeCompare(String(a.last_seen_at || ""));
    if (sort === "status") return String(a.status || "").localeCompare(String(b.status || ""));
    return String(b.created_at || "").localeCompare(String(a.created_at || ""));
  });
  const shown = sorted.slice(0, limit);

  return (
    <>
      <div className="flex gap-2 mb-3">
        <input value={q} onChange={(e) => { setQ(e.target.value); setLimit(50); }}
          placeholder="חיפוש לפי שם או אימייל" style={inputStyle} />
        <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="מיון"
          style={{ ...inputStyle, width: "auto", flexShrink: 0 }}>
          <option value="new">הצטרפו לאחרונה</option>
          <option value="active">פעילים לאחרונה</option>
          <option value="tenants">מספר דיירים</option>
          <option value="name">שם</option>
          <option value="status">סטטוס</option>
        </select>
        <button onClick={load} title="רענון" className="rounded-lg px-3 shrink-0"
          style={{ border: `1px solid ${C.line}`, background: C.card }}>
          <RefreshCw size={15} />
        </button>
      </div>

      {/* שורת המצב של הפלטפורמה. בלעדיה צריך לספור כרטיסים כדי לענות
          על "כמה אנשים באמת משתמשים בזה" — השאלה שבשבילה נכנסים לכאן. */}
      {accounts.length > 0 && (() => {
        const live = accounts.filter((x) => seenLabel(x.last_seen_at).tone === "live").length;
        const week = accounts.filter((x) => x.last_seen_at
          && Date.now() - new Date(x.last_seen_at).getTime() < 7 * 86400000).length;
        const never = accounts.filter((x) => !x.last_seen_at).length;
        const tenants = accounts.reduce((t, x) => t + (Number(x.tenants) || 0), 0);
        const apts = accounts.reduce((t, x) => t + (Number(x.apartments) || 0), 0);
        return (
          <div className="rounded-xl border p-3 mb-3 flex flex-wrap gap-x-5 gap-y-2 text-[12px]"
            style={{ background: C.card, borderColor: C.line }}>
            <span>
              <b className="text-base" style={{ color: live ? "#2E7D62" : C.ink }}>{live}</b>
              <span style={{ color: C.muted }}> מחוברים עכשיו</span>
            </span>
            <span>
              <b className="text-base">{week}</b>
              <span style={{ color: C.muted }}> נכנסו השבוע</span>
            </span>
            <span>
              <b className="text-base">{apts}</b>
              <span style={{ color: C.muted }}> נכסים · </span>
              <b className="text-base">{tenants}</b>
              <span style={{ color: C.muted }}> דיירים</span>
            </span>
            {never > 0 && (
              <span style={{ color: "#6B4E12" }}>
                <b className="text-base">{never}</b> נרשמו ולא נכנסו
              </span>
            )}
          </div>
        );
      })()}

      <p className="text-[12px] mb-2" style={{ color: C.muted }}>
        {shown.length < sorted.length
          ? `מוצגים ${shown.length} מתוך ${sorted.length}${needle ? ` שנמצאו (מ-${accounts.length})` : " חשבונות"}`
          : `${sorted.length} מתוך ${accounts.length} חשבונות`}
      </p>

      <div className="grid gap-2 sm:grid-cols-2">
        {shown.map((a) => (
          <AccountCard key={a.id} a={a} onOpen={() => setOpen(a)} />
        ))}
      </div>

      {shown.length < sorted.length && (
        <button onClick={() => setLimit(limit + 100)} className="mt-3 w-full rounded-lg py-2 text-sm"
          style={{ border: `1px solid ${C.line}`, background: C.card, color: C.teal }}>
          עוד 100 · נותרו {sorted.length - shown.length}
        </button>
      )}

      {open && (
        <PlanEditor account={open} onClose={() => setOpen(null)}
          onSaved={() => { setOpen(null); load(); }} />
      )}
    </>
  );
}

function AccountCard({ a, onOpen }) {
  const seen = seenLabel(a.last_seen_at);
  const trial = daysLeft(a.trial_ends_at);
  const paid = daysLeft(a.paid_until);
  const live = a.status === "active" && ((trial ?? -1) > 0 || (paid ?? -1) > 0);

  const badge = a.status === "suspended"
    ? { text: "מושעה", bg: "#FBEDE9", fg: "#7A2E1E" }
    : live
      ? { text: "פעיל", bg: "#E6F2EC", fg: C.paid }
      : { text: "קריאה בלבד", bg: "#FBF2E3", fg: "#6B4E12" };

  return (
    <button onClick={onOpen} className="rounded-xl border p-3 text-right w-full"
      style={{ background: C.card, borderColor: C.line }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium truncate">{a.name}</div>
          <div className="text-[12px] truncate" style={{ color: C.muted, direction: "ltr", textAlign: "right" }}>
            {a.owner_email}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* נקודה ירוקה = מחובר ממש עכשיו. זה מה שמחפשים ברשימה. */}
          {seen.tone === "live" && (
            <span className="rounded-full" style={{ width: 7, height: 7, background: "#2E7D62" }} aria-hidden="true" />
          )}
          <span className="rounded px-1.5 py-0.5 text-[11px]"
            style={{ background: badge.bg, color: badge.fg }}>
            {badge.text}
          </span>
        </div>
      </div>

      {/* שימוש בפועל: מתי נכנס, כמה נכסים, וכמה מהם מאוכלסים. מספרים
          בלבד — מסך הפלטפורמה לא רואה את הנתונים עצמם. */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[12px]">
        <span className="rounded px-1.5 py-0.5"
          style={{ background: SEEN_TONE[seen.tone].bg, color: SEEN_TONE[seen.tone].fg }}>
          {seen.text}
          {Number(a.live_sessions) > 1 ? ` · ${a.live_sessions} מכשירים` : ""}
        </span>
        <span style={{ color: C.muted }}>
          {Number(a.apartments) || 0} {Number(a.apartments) === 1 ? "נכס" : "נכסים"}
          {" · "}
          <b style={{ color: Number(a.tenants) ? C.ink : C.muted }}>
            {Number(a.tenants) || 0} {Number(a.tenants) === 1 ? "דייר" : "דיירים"}
          </b>
        </span>
        {Number(a.tenant_logins) > 0 && (
          <span style={{ color: C.muted }}>
            {a.tenant_logins} באזור האישי
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[12px]" style={{ color: C.muted }}>
        <span>
          {a.billing_interval === "yearly" ? "מנוי שנתי" : MODEL_LABEL[a.billing_model] || a.billing_model}
        </span>
        <span>
          {a.billing_interval === "yearly"
            ? `${shekels(a.price_agorot)} לשנה`
            : a.billing_model === "revenue_share"
              ? `${Number(a.revenue_pct)}%`
              : shekels(a.price_agorot)}
        </span>
        <span style={{ color: trial != null && trial <= 0 && !(paid > 0) ? C.late : C.muted }}>
          {paid > 0 ? `שולם עד ${heDate(a.paid_until)}`
            : trial == null ? "ללא ניסיון"
              : trial > 0 ? `${trial} ימי ניסיון` : "הניסיון הסתיים"}
        </span>
      </div>
    </button>
  );
}

/* ================================================= ברירות מחדל === */

/**
 * מה שכל נרשם חדש מקבל. זו התשובה ל"כמה זמן חינם" — היא נקבעת כאן
 * מראש, ולא לכל חשבון בנפרד אחרי שהוא כבר נרשם.
 */
function SettingsTab() {
  const [f, setF] = useState(null);
  const [env, setEnv] = useState({
    providers: {}, mailConfigured: false, smsConfigured: false,
    activeProvider: "link", smsProviders: {}, activeSmsProvider: null,
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [applyAsk, setApplyAsk] = useState(false);

  useEffect(() => {
    api.get("/billing/settings")
      .then((r) => {
        setEnv({
          providers: r.providers || {},
          mailConfigured: !!r.mailConfigured,
          smsConfigured: !!r.smsConfigured,
          activeProvider: r.activeProvider || "link",
          smsProviders: r.smsProviders || {},
          activeSmsProvider: r.activeSmsProvider || null,
        });
        setF({
          trialDays: String(r.trialDays),
          billingModel: r.billingModel,
          billingInterval: r.billingInterval || "monthly",
          price: String(r.priceAgorot / 100),
          price2y: String((r.price2yAgorot || 0) / 100),
          payLink1y: r.payLink1y || "",
          payLink2y: r.payLink2y || "",
          revenuePct: String(r.revenuePct),
          paymentProvider: r.paymentProvider || "auto",
          platformEmail: r.platformEmail || "",
          graceDays: String(r.graceDays ?? 0),
          remindDays1: String(r.remindDays1 ?? 14),
          remindDays2: String(r.remindDays2 ?? 7),
          remindDays3: String(r.remindDays3 ?? 3),
          notifyEmail: !!r.notifyEmail,
          notifySms: !!r.notifySms,
          smsProvider: r.smsProvider || "auto",
          feedbackEnabledOwners: r.feedbackEnabledOwners !== false,
          feedbackEnabledTenants: r.feedbackEnabledTenants !== false,
        });
      })
      .catch((e) => setMsg({ tone: "bad", text: e.message }));
  }, []);

  if (!f) return <Loader2 className="animate-spin" size={20} />;

  const yearly = f.billingInterval === "yearly";

  const body = () => ({
    trialDays: Number(f.trialDays || 0),
    billingInterval: f.billingInterval,
    /* במסלול שנתי המחיר תמיד קבוע — מודלים לפי יחידה/אחוז הם חודשיים. */
    billingModel: yearly ? "flat" : f.billingModel,
    priceAgorot: Math.round(Number(f.price || 0) * 100),
    price2yAgorot: Math.round(Number(f.price2y || 0) * 100),
    payLink1y: f.payLink1y.trim(),
    payLink2y: f.payLink2y.trim(),
    revenuePct: Number(f.revenuePct || 0),
    paymentProvider: f.paymentProvider,
    platformEmail: f.platformEmail.trim(),
    graceDays: Number(f.graceDays || 0),
    remindDays1: Number(f.remindDays1 || 0),
    remindDays2: Number(f.remindDays2 || 0),
    remindDays3: Number(f.remindDays3 || 0),
    notifyEmail: f.notifyEmail,
    notifySms: f.notifySms,
    smsProvider: f.smsProvider,
    feedbackEnabledOwners: f.feedbackEnabledOwners,
    feedbackEnabledTenants: f.feedbackEnabledTenants,
  });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      await api.put("/billing/settings", body());
      setMsg({ tone: "ok", text: "נשמר. יחול על נרשמים חדשים." });
    } catch (ex) {
      setMsg({ tone: "bad", text: ex.message });
    }
    setBusy(false);
  };

  /* "גם הוותיקים": שמירה ואז החלה על כל החשבונות הקיימים. */
  const applyAll = async () => {
    setBusy(true); setMsg(null); setApplyAsk(false);
    try {
      await api.put("/billing/settings", body());
      const r = await api.post("/billing/settings/apply", {});
      setMsg({ tone: "ok", text: `נשמר והוחל על ${r.updated} חשבונות קיימים (וימשיך לחול על נרשמים חדשים).` });
    } catch (ex) {
      setMsg({ tone: "bad", text: ex.message });
    }
    setBusy(false);
  };

  return (
    <form onSubmit={save} className="rounded-xl border p-4"
      style={{ background: C.card, borderColor: C.line }}>
      <div className="flex items-center gap-2 mb-1">
        <Settings2 size={16} style={{ color: C.teal }} />
        <h2 className="text-lg font-bold">תוכנית ברירת המחדל</h2>
      </div>
      <p className="text-[12px] mb-4" style={{ color: C.muted }}>
        "שמירה" חלה על נרשמים חדשים בלבד. "שמירה והחלה על כולם" מעדכנת גם
        את כל החשבונות הקיימים. לתנאים חריגים לחשבון בודד — לשונית "חשבונות".
      </p>

      {msg && <Notice text={msg.text} tone={msg.tone} />}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="תקופה חינם (ימים)" hint="0 = בלי ניסיון · 365 = שנה חינם ואז תשלום">
          <input type="number" min="0" max="3650" inputMode="numeric" style={inputStyle}
            value={f.trialDays} onChange={(e) => setF({ ...f, trialDays: e.target.value })} />
        </Field>

        <Field label="מחזור חיוב">
          <select style={inputStyle} value={f.billingInterval}
            onChange={(e) => setF({ ...f, billingInterval: e.target.value })}>
            <option value="monthly">חודשי</option>
            <option value="yearly">שנתי — תשלום אחד לשנה</option>
          </select>
        </Field>

        {yearly ? (
          <>
            <Field label="₪ לשנה">
              <input type="number" step="1" min="0" inputMode="numeric" style={inputStyle}
                value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
            </Field>
            <Field label="₪ לשנתיים (מבצע)" hint="0 = בלי מבצע שנתיים">
              <input type="number" step="1" min="0" inputMode="numeric" style={inputStyle}
                value={f.price2y} onChange={(e) => setF({ ...f, price2y: e.target.value })} />
            </Field>
            <div className="sm:col-span-2 grid gap-3 sm:grid-cols-2">
              <Field label="קישור עמוד תשלום — שנה"
                hint="עמוד סליקה חיצוני (Grow / ביט עסקי / PayPlus). מיותר אם מפתחות PayPlus מוגדרים בשרת.">
                <input dir="ltr" style={inputStyle} placeholder="https://…"
                  value={f.payLink1y} onChange={(e) => setF({ ...f, payLink1y: e.target.value })} />
              </Field>
              <Field label="קישור עמוד תשלום — שנתיים">
                <input dir="ltr" style={inputStyle} placeholder="https://…"
                  value={f.payLink2y} onChange={(e) => setF({ ...f, payLink2y: e.target.value })} />
              </Field>
            </div>
          </>
        ) : (
          <>
            <Field label="מודל תמחור אחרי הניסיון">
              <select style={inputStyle} value={f.billingModel}
                onChange={(e) => setF({ ...f, billingModel: e.target.value })}>
                <option value="flat">מחיר קבוע לחודש</option>
                <option value="per_unit">מחיר ליחידה מאוכלסת</option>
                <option value="revenue_share">אחוז מהגבייה</option>
              </select>
            </Field>

            {f.billingModel === "revenue_share" ? (
              <Field label="אחוז מהגבייה" hint="נגבה רק ממה שהתקבל בפועל">
                <input type="number" step="0.25" min="0" max="100" inputMode="decimal"
                  style={inputStyle} value={f.revenuePct}
                  onChange={(e) => setF({ ...f, revenuePct: e.target.value })} />
              </Field>
            ) : (
              <Field label={f.billingModel === "per_unit" ? "₪ ליחידה לחודש" : "₪ לחודש"}>
                <input type="number" step="1" min="0" inputMode="numeric" style={inputStyle}
                  value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
              </Field>
            )}
          </>
        )}
      </div>

      {/* ------------------------------------------------------ סליקה --- */}
      <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${C.line}` }}>
        <h3 className="text-sm font-bold mb-1">ספק הסליקה</h3>
        <p className="text-[12px] mb-3" style={{ color: C.muted }}>
          מי יוצר את עמוד התשלום ומאשר עסקאות. מפתחות הספקים מוגדרים בשרת;
          כאן רק בוחרים מי פעיל — ההחלפה מיידית, בלי פריסה.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="ספק פעיל">
            <select style={inputStyle} value={f.paymentProvider}
              onChange={(e) => setF({ ...f, paymentProvider: e.target.value })}>
              <option value="auto">אוטומטי — הסולק הראשון שמוגדר בשרת</option>
              <option value="payplus" disabled={!env.providers.payplus?.configured}>
                PayPlus {env.providers.payplus?.configured ? "" : "(מפתחות לא מוגדרים)"}
              </option>
              <option value="payme" disabled={!env.providers.payme?.configured}>
                PayMe {env.providers.payme?.configured ? "" : "(מפתחות לא מוגדרים)"}
              </option>
              <option value="link">קישור ידני — אישור תשלומים בלחיצה</option>
            </select>
          </Field>
          <div className="text-[12px] self-end pb-1" style={{ color: C.muted }}>
            {["payplus", "payme"].map((id) => (
              <div key={id}>
                {env.providers[id]?.label || id}:{" "}
                <span style={{ color: env.providers[id]?.configured ? C.paid : C.late }}>
                  {env.providers[id]?.configured ? "מפתחות מוגדרים בשרת ✓" : "לא מוגדר"}
                </span>
              </div>
            ))}
            {f.paymentProvider === "auto" && (
              <div className="mt-0.5">
                כרגע פעיל בפועל: <strong>{env.providers[env.activeProvider]?.label || env.activeProvider}</strong>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --------------------------------------------- התראות תשלום --- */}
      <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${C.line}` }}>
        <h3 className="text-sm font-bold mb-1">התראות תשלום ומדרגות</h3>
        <p className="text-[12px] mb-3" style={{ color: C.muted }}>
          שלוש תזכורות לפני סיום הניסיון או המנוי, התראה ביום הסיום, ואז ימי
          חסד שבהם עוד אפשר לערוך — ורק בסופם מעבר לצפייה בלבד.
          {" "}מייל: {env.mailConfigured
            ? <span style={{ color: C.paid }}>מוגדר בשרת ✓</span>
            : <span style={{ color: C.late }}>שירות המייל לא מוגדר בשרת — התראות יופיעו רק בתוך המערכת</span>}
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="המייל של בעל הפלטפורמה"
            hint='כתובת המענה: "השב" על התראת תשלום מגיע לכאן'>
            <input dir="ltr" type="email" style={inputStyle} placeholder="you@example.com"
              value={f.platformEmail} onChange={(e) => setF({ ...f, platformEmail: e.target.value })} />
          </Field>

          <div className="flex flex-col justify-end gap-1.5 pb-1">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.notifyEmail}
                onChange={(e) => setF({ ...f, notifyEmail: e.target.checked })} />
              שליחת התראות במייל
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.notifySms}
                onChange={(e) => setF({ ...f, notifySms: e.target.checked })} />
              שליחת התראות בהודעת טלפון
            </label>
          </div>

          <Field label="ספק הודעות הטלפון"
            hint="המפתחות מוגדרים בשרת; כאן רק בוחרים מי פעיל">
            <select style={inputStyle} value={f.smsProvider}
              onChange={(e) => setF({ ...f, smsProvider: e.target.value })}>
              <option value="auto">אוטומטי — הספק הראשון שמוגדר בשרת</option>
              {Object.entries(env.smsProviders).map(([id, p]) => (
                <option key={id} value={id} disabled={!p.configured}>
                  {p.label} {p.configured ? "" : "(מפתחות לא מוגדרים)"}
                </option>
              ))}
            </select>
          </Field>

          <div className="text-[12px] self-end pb-1" style={{ color: C.muted }}>
            {Object.entries(env.smsProviders).map(([id, p]) => (
              <div key={id}>
                {p.label}:{" "}
                <span style={{ color: p.configured ? C.paid : C.late }}>
                  {p.configured ? "מפתחות מוגדרים בשרת ✓" : "לא מוגדר"}
                </span>
              </div>
            ))}
            <div className="mt-0.5">
              {env.activeSmsProvider
                ? <>כרגע פעיל בפועל: <strong>{env.smsProviders[env.activeSmsProvider]?.label || env.activeSmsProvider}</strong></>
                : "אף ספק לא מוגדר — התראות הטלפון כבויות עד שמזינים מפתחות בשרת."}
            </div>
          </div>

          <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="תזכורת ראשונה" hint="ימים לפני הסיום">
              <input type="number" min="0" max="365" inputMode="numeric" style={inputStyle}
                value={f.remindDays1} onChange={(e) => setF({ ...f, remindDays1: e.target.value })} />
            </Field>
            <Field label="תזכורת שנייה" hint="ימים לפני הסיום">
              <input type="number" min="0" max="365" inputMode="numeric" style={inputStyle}
                value={f.remindDays2} onChange={(e) => setF({ ...f, remindDays2: e.target.value })} />
            </Field>
            <Field label="תזכורת שלישית" hint="ימים לפני הסיום">
              <input type="number" min="0" max="365" inputMode="numeric" style={inputStyle}
                value={f.remindDays3} onChange={(e) => setF({ ...f, remindDays3: e.target.value })} />
            </Field>
            <Field label="ימי חסד" hint="אחרי הסיום, לפני צפייה בלבד">
              <input type="number" min="0" max="365" inputMode="numeric" style={inputStyle}
                value={f.graceDays} onChange={(e) => setF({ ...f, graceDays: e.target.value })} />
            </Field>
          </div>
        </div>
        <p className="text-[11px] mt-2" style={{ color: C.muted }}>
          התראה ביום הסיום נשלחת תמיד. חשבון שחידש — המדרגות נדרכות מחדש
          לקראת הפקיעה הבאה.
        </p>

        {/* שני מתגים נפרדים: אפשר לסגור לדיירים בלי לסגור לבעלי נכסים ולהפך.
            השיחות עצמן נמצאות בלשונית "הצעות לשיפור". */}
        <div className="flex flex-col gap-1.5 mt-3 pt-3 border-t" style={{ borderColor: C.line }}>
          <div className="text-sm" style={{ fontWeight: 600 }}>הצעות לשיפור מהמשתמשים</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.feedbackEnabledOwners}
              onChange={(e) => setF({ ...f, feedbackEnabledOwners: e.target.checked })} />
            לקבל הצעות מבעלי נכסים
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.feedbackEnabledTenants}
              onChange={(e) => setF({ ...f, feedbackEnabledTenants: e.target.checked })} />
            לקבל הצעות מדיירים
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <button type="submit" disabled={busy}
          className="rounded-xl px-5 py-2.5 text-sm"
          style={{ background: C.teal, color: "#fff", opacity: busy ? 0.7 : 1 }}>
          {busy ? "שומר…" : "שמירה (נרשמים חדשים)"}
        </button>
        <button type="button" disabled={busy} onClick={() => setApplyAsk(true)}
          className="rounded-xl px-5 py-2.5 text-sm"
          style={{ border: `1px solid ${C.teal}`, color: C.teal, opacity: busy ? 0.7 : 1 }}>
          שמירה והחלה על כולם
        </button>
      </div>

      {applyAsk && (
        <div className="mt-3 rounded-xl border p-3" style={{ borderColor: C.ochre, background: "#FBF2E3" }}>
          <p className="text-sm mb-2">
            להחיל את התוכנית על <strong>כל החשבונות הקיימים</strong>? המחיר ומחזור
            החיוב יתעדכנו לכולם. תקופות ניסיון שרצות ומנויים ששולמו לא נפגעים —
            המחיר החדש פוגש כל משתמש רק בתשלום הבא שלו.
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={applyAll}
              className="rounded-lg px-4 py-2 text-sm" style={{ background: C.teal, color: "#fff" }}>
              כן, החל על כולם
            </button>
            <button type="button" onClick={() => setApplyAsk(false)}
              className="rounded-lg px-4 py-2 text-sm" style={{ border: `1px solid ${C.line}` }}>
              ביטול
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

/* ============================================== הצעות לשיפור === */
/* ערוץ נפרד לגמרי מתיבת ההודעות הרגילה של דייר↔בעל-נכס: כאן זה
   משתמש קצה (בעל נכס או דייר) מול בעל הפלטפורמה. */

function Feedback() {
  const [threads, setThreads] = useState(null);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setThreads(await api.get("/billing/feedback/threads")); setErr(""); }
    catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openThread = async (id) => {
    setOpenId(id); setThread(null);
    try {
      setThread(await api.get(`/billing/feedback/threads/${id}`));
      await api.post(`/billing/feedback/threads/${id}/read`, {});
      load(); // מרענן את מונה הלא-נקראות ברשימה
    } catch (e) { setErr(e.message); }
  };

  const send = async () => {
    const t = text.trim();
    if (!t || !openId || busy) return;
    setBusy(true);
    try {
      await api.post(`/billing/feedback/threads/${openId}/reply`, { text: t });
      setThread(await api.get(`/billing/feedback/threads/${openId}`));
      setText("");
      load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  if (err) return <Notice text={err} />;
  if (threads === null) return <Loader2 className="animate-spin" size={20} />;

  const heDateTime = (d) => (d ? new Date(d).toLocaleString("he-IL") : "");

  return (
    <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
      <div className="space-y-1.5">
        {threads.length === 0 && (
          <p className="text-sm" style={{ color: C.muted }}>עדיין לא התקבלו הצעות.</p>
        )}
        {threads.map((t) => (
          <button key={t.id} onClick={() => openThread(t.id)}
            className="w-full text-right rounded-lg border p-2.5 text-sm"
            style={{
              borderColor: openId === t.id ? C.teal : C.line,
              background: openId === t.id ? "#EAF3EE" : C.card,
            }}>
            <div className="flex items-center justify-between gap-2">
              <span style={{ fontWeight: 600 }}>{t.author}</span>
              <span className="text-[10px] rounded-full px-1.5 py-0.5 shrink-0"
                style={t.source === "owner"
                  ? { background: "#FBF2E3", color: "#6B4E12" }
                  : { background: "#EAF3EE", color: C.paid }}>
                {t.source === "owner" ? "בעל נכס" : "דייר"}
              </span>
            </div>
            <div className="text-[12px] mt-1 truncate" style={{ color: C.muted }}>
              {t.lastFromAdmin ? "אתה: " : ""}{t.lastText}
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10px]" style={{ color: C.muted }}>{heDateTime(t.lastAt)}</span>
              {t.unread > 0 && (
                <span className="text-[10px] rounded-full px-1.5 py-0.5" style={{ background: C.teal, color: "#fff" }}>
                  {t.unread}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="rounded-lg border p-3" style={{ borderColor: C.line, minHeight: 200 }}>
        {!openId ? (
          <p className="text-sm" style={{ color: C.muted }}>בחרו הצעה מהרשימה כדי לקרוא ולהשיב.</p>
        ) : thread === null ? (
          <Loader2 className="animate-spin" size={18} />
        ) : (
          <>
            <div className="space-y-1.5 mb-3 max-h-96 overflow-y-auto">
              {thread.messages.map((m) => (
                <div key={m.id} className="text-sm rounded-lg px-2.5 py-1.5"
                  style={{ background: m.fromAdmin ? "#EAF3EE" : "#F1F4F1" }}>
                  <div className="text-[10px] mb-0.5" style={{ color: C.muted }}>
                    {m.fromAdmin ? "אתה" : thread.author} · {heDateTime(m.createdAt)}
                  </div>
                  {m.text}
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
                placeholder="תגובה…" className="flex-1 rounded-lg p-2 text-sm" style={inputStyle} />
              <button onClick={send} disabled={busy || !text.trim()}
                className="rounded-lg px-3 py-2 text-sm shrink-0"
                style={{ background: C.teal, color: "#fff", opacity: busy || !text.trim() ? 0.6 : 1 }}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : "שליחה"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ================================================== תשלומים === */

/**
 * תשלומי מנוי שמשתמשים פתחו. במצב "קישור ידני" זה המקום שבו תשלום
 * שבוצע אצל הסולק הופך למנוי: רואים את הבקשה, בודקים מול חשבון הסולק,
 * ומאשרים — האישור מאריך את המנוי בשנה או שנתיים ומחזיר חשבון
 * מקריאה-בלבד לפעיל. עם PayPlus מחובר, תשלומים מאושרים לבד ומופיעים
 * כאן כסגורים.
 */
function Payments() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      setRows((await api.get("/billing/payments")).payments);
      setErr("");
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, verb) => {
    setBusy(id); setErr("");
    try { await api.post(`/billing/payments/${id}/${verb}`, {}); await load(); }
    catch (e) { setErr(e.message); }
    setBusy("");
  };

  if (err && rows === null) return <Notice text={err} />;
  if (rows === null) return <Loader2 className="animate-spin" size={20} />;

  const pending = rows.filter((p) => p.status === "pending");
  const closed = rows.filter((p) => p.status !== "pending");

  return (
    <div>
      {err && <Notice text={err} />}

      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold">ממתינים לאישור ({pending.length})</h2>
        <button onClick={load} title="רענון" className="rounded-lg px-2.5 py-1.5"
          style={{ border: `1px solid ${C.line}`, background: C.card }}>
          <RefreshCw size={14} />
        </button>
      </div>

      {!pending.length && (
        <p className="text-[12px] mb-4" style={{ color: C.muted }}>
          אין תשלומים ממתינים. כשמשתמש לוחץ "לתשלום" הבקשה תופיע כאן,
          ואחרי שהכסף נראה אצל הסולק — מאשרים בלחיצה.
        </p>
      )}

      <div className="grid gap-2 mb-5">
        {pending.map((p) => (
          <div key={p.id} className="rounded-xl border p-3" style={{ background: C.card, borderColor: C.ochre }}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium truncate">{p.account_name}</div>
                <div className="text-[12px] truncate" style={{ color: C.muted, direction: "ltr", textAlign: "right" }}>
                  {p.owner_email}
                </div>
              </div>
              <div className="text-left shrink-0">
                <div className="font-bold">{shekels(p.amount_agorot)}</div>
                <div className="text-[11px]" style={{ color: C.muted }}>{PLAN_LABEL[p.plan] || p.plan}</div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px]" style={{ color: C.muted }}>
                נפתח {heDate(p.created_at)} · {
                  { payplus: "PayPlus", payme: "PayMe", manual: "אישור ידני" }[p.provider] || "קישור תשלום"
                }
              </span>
              <div className="flex gap-2">
                <button onClick={() => act(p.id, "confirm")} disabled={busy === p.id}
                  className="text-xs rounded-lg px-3 py-1.5"
                  style={{ background: C.teal, color: "#fff", opacity: busy === p.id ? 0.7 : 1 }}>
                  {busy === p.id ? "…" : `אישור — הארכה ב${p.plan === "2y" ? "שנתיים" : "שנה"}`}
                </button>
                <button onClick={() => act(p.id, "cancel")} disabled={busy === p.id}
                  className="text-xs rounded-lg px-3 py-1.5" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
                  ביטול
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {closed.length > 0 && (
        <>
          <h2 className="text-sm font-bold mb-2">היסטוריה</h2>
          <div className="grid gap-1.5">
            {closed.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-[12px] rounded-lg px-2.5 py-2"
                style={{ background: C.card, border: `1px solid ${C.line}` }}>
                <span className="font-medium truncate">{p.account_name}</span>
                <span style={{ color: C.muted }}>{PLAN_LABEL[p.plan] || p.plan}</span>
                <span>{shekels(p.amount_agorot)}</span>
                <span style={{ color: p.status === "paid" ? C.paid : C.muted }}>
                  {p.status === "paid" ? `שולם ${heDate(p.paid_at)}` : "בוטל"}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ============================================== עריכת חשבון === */

const STAGE_LABEL = {
  remind_1: "תזכורת ראשונה",
  remind_2: "תזכורת שנייה",
  remind_3: "תזכורת שלישית",
  expired: "התראת יום הסיום",
  read_only: "מעבר לצפייה בלבד",
};

function PlanEditor({ account, onClose, onSaved }) {
  // הודעה לבעל החשבון ומחיקת החשבון — פעולות הנהלה על חשבון אחד.
  const [noticeText, setNoticeText] = useState("");
  const [delName, setDelName] = useState("");
  const [delReason, setDelReason] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const [f, setF] = useState({
    billingModel: account.billing_model,
    billingInterval: account.billing_interval || "monthly",
    price: String(Math.round(account.price_agorot) / 100),
    price2y: String(Math.round(account.price_2y_agorot || 0) / 100),
    revenuePct: String(Number(account.revenue_pct)),
    trialDays: "",
    paidUntil: account.paid_until ? String(account.paid_until).slice(0, 10) : "",
    status: account.status,
    ownerPhone: account.owner_phone || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [periods, setPeriods] = useState(null);
  const [notices, setNotices] = useState(null);

  useEffect(() => {
    api.get(`/billing/accounts/${account.id}/periods`)
      .then((r) => setPeriods(r.periods))
      .catch(() => setPeriods([]));
    api.get(`/billing/accounts/${account.id}/notices`)
      .then((r) => setNotices(r.notices))
      .catch(() => setNotices([]));
  }, [account.id]);

  const yearly = f.billingInterval === "yearly";

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = {
        billingModel: yearly ? "flat" : f.billingModel,
        billingInterval: f.billingInterval,
        priceAgorot: Math.round(Number(f.price || 0) * 100),
        price2yAgorot: Math.round(Number(f.price2y || 0) * 100),
        revenuePct: Number(f.revenuePct || 0),
        status: f.status,
        paidUntil: f.paidUntil ? new Date(f.paidUntil + "T23:59:59").toISOString() : null,
        ownerPhone: f.ownerPhone.trim(),
      };
      if (f.trialDays !== "") body.trialDays = Number(f.trialDays);
      await api.put(`/billing/accounts/${account.id}`, body);
      onSaved();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  const remindNow = async () => {
    setBusy(true); setErr(""); setNote("");
    try {
      const r = await api.post(`/billing/accounts/${account.id}/remind`, {});
      setNote(r.message || "נשלח.");
      const n = await api.get(`/billing/accounts/${account.id}/notices`);
      setNotices(n.notices);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  /* קיצור ל"שולם עד": שנה/שנתיים מסוף המנוי הקיים אם הוא בתוקף, מהיום
     אם פג — אותו כלל כמו האישור בשרת. */
  const extend = (years) => {
    const today = new Date();
    const cur = f.paidUntil ? new Date(f.paidUntil + "T12:00:00") : null;
    const base = cur && cur > today ? cur : today;
    const d = new Date(base);
    d.setFullYear(d.getFullYear() + years);
    setF({ ...f, paidUntil: d.toISOString().slice(0, 10) });
  };

  const closePeriod = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/billing/accounts/${account.id}/close`, {});
      setPeriods((p) => [r.period, ...(p || []).filter((x) => x.period !== r.period.period)]);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div dir="rtl" className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: "rgba(20,38,43,0.45)" }}>
      {/* בטלפון החלון תופס את כל הרוחב ונדבק לתחתית; במסך רחב הוא מרוכז */}
      <div className="min-h-full flex items-end sm:items-center justify-center sm:p-6">
        <div className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl border p-5"
          style={{ background: C.card, borderColor: C.line }}>
          <h2 className="text-lg font-bold">{account.name}</h2>
          <p className="text-sm mb-4 truncate"
            style={{ color: C.muted, direction: "ltr", textAlign: "right" }}>
            {account.owner_email}
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="מחזור חיוב">
              <select value={f.billingInterval} style={inputStyle}
                onChange={(e) => setF({ ...f, billingInterval: e.target.value })}>
                <option value="monthly">חודשי</option>
                <option value="yearly">שנתי — תשלום אחד לשנה</option>
              </select>
            </Field>

            <Field label="מצב">
              <select value={f.status} style={inputStyle}
                onChange={(e) => setF({ ...f, status: e.target.value })}>
                <option value="active">פעיל</option>
                <option value="read_only">קריאה בלבד</option>
                <option value="suspended">מושעה</option>
              </select>
            </Field>

            {yearly ? (
              <>
                <Field label="₪ לשנה">
                  <input type="number" step="1" min="0" inputMode="numeric" style={inputStyle}
                    value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
                </Field>
                <Field label="₪ לשנתיים (מבצע)" hint="0 = בלי מבצע">
                  <input type="number" step="1" min="0" inputMode="numeric" style={inputStyle}
                    value={f.price2y} onChange={(e) => setF({ ...f, price2y: e.target.value })} />
                </Field>
              </>
            ) : (
              <>
                <Field label="מודל תמחור">
                  <select value={f.billingModel} style={inputStyle}
                    onChange={(e) => setF({ ...f, billingModel: e.target.value })}>
                    <option value="flat">מחיר קבוע לחודש</option>
                    <option value="per_unit">מחיר ליחידה מאוכלסת</option>
                    <option value="revenue_share">אחוז מהגבייה</option>
                  </select>
                </Field>

                {f.billingModel === "revenue_share" ? (
                  <Field label="אחוז מהגבייה">
                    <input type="number" step="0.25" min="0" max="100" inputMode="decimal"
                      style={inputStyle} value={f.revenuePct}
                      onChange={(e) => setF({ ...f, revenuePct: e.target.value })} />
                  </Field>
                ) : (
                  <Field label={f.billingModel === "per_unit" ? "₪ ליחידה לחודש" : "₪ לחודש"}>
                    <input type="number" step="1" min="0" inputMode="numeric" style={inputStyle}
                      value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
                  </Field>
                )}
              </>
            )}

            <Field label="שולם עד" hint="אישור תשלום ידני: הוסיפו שנה/שנתיים ושמרו">
              <input type="date" style={inputStyle} value={f.paidUntil}
                onChange={(e) => setF({ ...f, paidUntil: e.target.value })} />
              <div className="flex gap-1.5 mt-1.5">
                <button type="button" onClick={() => extend(1)}
                  className="text-[11px] rounded-lg px-2 py-1" style={{ border: `1px solid ${C.line}` }}>
                  + שנה
                </button>
                <button type="button" onClick={() => extend(2)}
                  className="text-[11px] rounded-lg px-2 py-1" style={{ border: `1px solid ${C.line}` }}>
                  + שנתיים
                </button>
              </div>
            </Field>

            <div className="sm:col-span-2">
              <Field label="שינוי ניסיון (ימים מהיום)"
                hint="ריק = ללא שינוי · 0 = לסיים את הניסיון עכשיו">
                <input type="number" inputMode="numeric" placeholder="ללא שינוי"
                  style={inputStyle} value={f.trialDays}
                  onChange={(e) => setF({ ...f, trialDays: e.target.value })} />
              </Field>
            </div>

            <div className="sm:col-span-2">
              <Field label="טלפון בעל החשבון" hint="להתראות בהודעת טלפון. ריק = אין">
                <input dir="ltr" type="tel" placeholder="05x-xxxxxxx"
                  style={inputStyle} value={f.ownerPhone}
                  onChange={(e) => setF({ ...f, ownerPhone: e.target.value })} />
              </Field>
            </div>
          </div>

          {err && <div className="mt-3"><Notice text={err} /></div>}
          {note && <div className="mt-3"><Notice text={note} tone="ok" /></div>}

          <div className="flex gap-2 mt-4">
            <button onClick={save} disabled={busy} className="flex-1 rounded-xl py-3 text-sm"
              style={{ background: C.teal, color: "#fff", opacity: busy ? 0.7 : 1 }}>
              {busy ? "שומר…" : "שמירה"}
            </button>
            <button onClick={onClose} className="rounded-xl px-4 py-3 text-sm"
              style={{ border: `1px solid ${C.line}` }}>
              סגירה
            </button>
          </div>

          {/* הודעה לבעל החשבון — לתיבת ההודעות שלו (עם צליל) וגם למייל כשמוגדר. */}
          <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${C.line}` }}>
            <h3 className="text-sm font-bold mb-1">הודעה לבעל החשבון</h3>
            <p className="text-[11px] mb-2" style={{ color: C.muted }}>
              נכנסת ללשונית "הודעות" שלו עם צליל, ונשלחת גם למייל כשהמייל מוגדר בשרת.
            </p>
            <textarea value={noticeText} rows={3} maxLength={2000} onChange={(e) => setNoticeText(e.target.value)}
              placeholder="למשל: המנוי שלך מסתיים בעוד שבוע — נשמח שתחדש כדי שהחשבון לא יעבור לקריאה בלבד."
              style={{ ...inputStyle, resize: "vertical" }} />
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px]" style={{ color: C.muted }}>{noticeText.length}/2000</span>
              <button disabled={busy || !noticeText.trim()}
                onClick={async () => {
                  setBusy(true); setErr(""); setNote("");
                  try {
                    const r = await api.post(`/billing/accounts/${account.id}/notify`, { text: noticeText.trim() });
                    setNote(r.message || "נשלח.");
                    setNoticeText("");
                  } catch (e) { setErr(e.message); }
                  setBusy(false);
                }}
                className="rounded-lg px-4 py-2 text-sm"
                style={{ background: C.teal, color: "#fff", opacity: busy || !noticeText.trim() ? 0.6 : 1 }}>
                שליחה
              </button>
            </div>
          </div>

          {/* מחיקת חשבון — לצמיתות. הקלדת שם החשבון היא האישור. */}
          <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${C.line}` }}>
            <h3 className="text-sm font-bold mb-1" style={{ color: C.late }}>מחיקת החשבון</h3>
            <p className="text-[11px] mb-2" style={{ color: C.muted }}>
              מוחקת לצמיתות את החשבון וכל מה שבו — נכסים, דיירים, תשלומים, קבצים, הודעות ותשלומי מנוי.
              בעל החשבון מקבל מייל על המחיקה (כשהמייל מוגדר). אין דרך חזרה.
            </p>
            {!delOpen ? (
              <button onClick={() => { setDelOpen(true); setDelName(""); }} className="text-sm rounded-lg px-3 py-2"
                style={{ border: `1px solid ${C.late}`, color: C.late }}>
                מחיקת החשבון…
              </button>
            ) : (
              <div className="rounded-lg p-3" style={{ background: "#FBEDE9" }}>
                <label className="text-xs block mb-1" style={{ color: "#7A2E1E" }}>
                  כדי לאשר, הקלידו את שם החשבון בדיוק: <b>{account.name}</b>
                </label>
                <input value={delName} onChange={(e) => setDelName(e.target.value)} placeholder={account.name}
                  style={{ ...inputStyle, background: "#fff", marginBottom: 8 }} />
                <input value={delReason} onChange={(e) => setDelReason(e.target.value)} placeholder="סיבה (לא חובה) — תופיע במייל לבעל החשבון"
                  style={{ ...inputStyle, background: "#fff", marginBottom: 8 }} />
                <div className="flex flex-wrap gap-2">
                  <button disabled={busy || delName.trim() !== String(account.name || "").trim()}
                    onClick={async () => {
                      if (!window.confirm(`למחוק לצמיתות את "${account.name}"? אין דרך חזרה.`)) return;
                      setBusy(true); setErr("");
                      try {
                        await api.del(`/billing/accounts/${account.id}`, { reason: delReason.trim() });
                        onSaved();
                      } catch (e) { setErr(e.message); setBusy(false); }
                    }}
                    className="text-sm rounded-lg px-3 py-2"
                    style={{ background: C.late, color: "#fff", opacity: busy || delName.trim() !== String(account.name || "").trim() ? 0.45 : 1 }}>
                    כן, למחוק לצמיתות
                  </button>
                  <button onClick={() => { setDelOpen(false); setDelName(""); }} className="text-sm rounded-lg px-3 py-2"
                    style={{ border: `1px solid ${C.line}` }}>ביטול</button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${C.line}` }}>
            <div className="flex items-center justify-between mb-2 gap-2">
              <h3 className="text-sm font-bold">תקופות חיוב</h3>
              <button onClick={closePeriod} disabled={busy}
                className="text-xs rounded-lg px-2.5 py-1.5 shrink-0"
                style={{ border: `1px solid ${C.line}` }}>
                סגירת החודש
              </button>
            </div>

            {periods === null ? <Loader2 className="animate-spin" size={16} />
              : !periods.length
                ? <p className="text-[12px]" style={{ color: C.muted }}>עוד לא נסגרה תקופה.</p>
                : (
                  <div className="grid gap-1.5">
                    {periods.map((p) => (
                      <div key={p.period}
                        className="flex items-center justify-between text-[12px] rounded-lg px-2.5 py-2"
                        style={{ background: "#FBFCFB" }}>
                        <span className="font-medium">{p.period}</span>
                        <span style={{ color: C.muted }}>{p.units} יח׳</span>
                        <span style={{ color: C.muted }}>{shekels(p.collected_agorot)}</span>
                        <span className="font-medium">{shekels(p.charge_agorot)}</span>
                      </div>
                    ))}
                  </div>
                )}
          </div>

          <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${C.line}` }}>
            <div className="flex items-center justify-between mb-2 gap-2">
              <h3 className="text-sm font-bold">התראות תשלום</h3>
              <button onClick={remindNow} disabled={busy}
                className="text-xs rounded-lg px-2.5 py-1.5 shrink-0"
                style={{ border: `1px solid ${C.line}` }}>
                שליחת תזכורת עכשיו
              </button>
            </div>

            {notices === null ? <Loader2 className="animate-spin" size={16} />
              : !notices.length
                ? <p className="text-[12px]" style={{ color: C.muted }}>
                    עוד לא נשלחו התראות לחשבון הזה.
                  </p>
                : (
                  <div className="grid gap-1.5">
                    {notices.map((n, i) => (
                      <div key={i}
                        className="flex items-center justify-between text-[12px] rounded-lg px-2.5 py-2"
                        style={{ background: "#FBFCFB" }}>
                        <span className="font-medium">{STAGE_LABEL[n.stage] || n.stage}</span>
                        <span style={{ color: C.muted }}>{n.channel === "email" ? "מייל" : n.channel === "sms" ? "טלפון" : n.channel}</span>
                        <span style={{ color: C.muted }}>{heDate(n.sent_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================== השוואה === */

function Compare() {
  const [p, setP] = useState({ flat: 49, perUnit: 9, pct: 2 });
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const run = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const q = new URLSearchParams({
        flat: String(Math.round(p.flat * 100)),
        perUnit: String(Math.round(p.perUnit * 100)),
        pct: String(p.pct),
      });
      setR(await api.get(`/billing/compare?${q}`));
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }, [p]);

  useEffect(() => { run(); /* פעם אחת בטעינה */ }, []); // eslint-disable-line

  return (
    <div>
      <div className="rounded-xl border p-4 mb-3" style={{ background: C.card, borderColor: C.line }}>
        <div className="grid grid-cols-3 gap-2">
          {[["flat", "₪ קבוע"], ["perUnit", "₪ ליחידה"], ["pct", "% מהגבייה"]].map(([k, label]) => (
            <Field key={k} label={label}>
              <input type="number" step={k === "pct" ? "0.25" : "1"} min="0"
                inputMode="decimal" style={inputStyle} value={p[k]}
                onChange={(e) => setP({ ...p, [k]: Number(e.target.value) })} />
            </Field>
          ))}
        </div>
        <button onClick={run} disabled={busy}
          className="mt-3 w-full sm:w-auto rounded-lg px-4 py-2.5 text-sm"
          style={{ background: C.teal, color: "#fff" }}>
          {busy ? "מחשב…" : "חישוב"}
        </button>
      </div>

      {err && <Notice text={err} />}

      {r && (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[
              ["קבוע", r.totals.flat],
              ["לפי יחידה", r.totals.per_unit],
              ["אחוז", r.totals.revenue_share],
            ].map(([label, v]) => (
              <div key={label} className="rounded-xl border p-3"
                style={{ background: C.card, borderColor: C.line }}>
                <div className="text-[11px]" style={{ color: C.muted }}>{label}</div>
                <div className="text-lg sm:text-2xl font-bold">{shekels(v)}</div>
              </div>
            ))}
          </div>

          <p className="text-[12px] mb-3" style={{ color: C.muted }}>
            {r.period} · {r.totals.units} יחידות מאוכלסות · {shekels(r.totals.collected)} נגבו בפועל
          </p>

          <div className="grid gap-2">
            {r.accounts.filter((a) => a.units > 0).map((a) => (
              <div key={a.accountId} className="rounded-xl border p-3"
                style={{ background: C.card, borderColor: C.line }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{a.name}</span>
                  <span className="text-[12px] shrink-0" style={{ color: C.muted }}>
                    {a.units} יח׳ · {shekels(a.collectedAgorot)}
                  </span>
                </div>
                <div className="flex gap-3 mt-1.5 text-[12px]">
                  <span>קבוע {shekels(a.prices.flat)}</span>
                  <span>יחידה {shekels(a.prices.per_unit)}</span>
                  <span>אחוז {shekels(a.prices.revenue_share)}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ============================================== משפך הרשמה === */

const FUNNEL_STAGE_LABEL = {
  "view": "פתחו את מסך ההרשמה",
  "field_focus:name": "מילאו שם",
  "field_focus:email": "מילאו אימייל",
  "field_focus:password": "מילאו סיסמה",
  "field_focus:agree": "אישרו תנאים",
  "submit_attempt": "שלחו את הטופס",
  "submit_result:success": "נרשמו בהצלחה",
};

const FUNNEL_REASON_LABEL = {
  password_too_short: "סיסמה קצרה מדי",
  terms_not_accepted: "לא אישרו תנאים",
  email_taken: "אימייל כבר רשום",
  rate_limited: "יותר מדי ניסיונות",
  server_error: "שגיאת שרת",
};

function SignupFunnel() {
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/billing/signup-funnel?days=30")
      .then(setR)
      .catch((e) => setErr(e.message));
  }, []);

  if (err) return <Notice text={err} />;
  if (!r) return <div className="text-sm" style={{ color: C.muted }}>טוען…</div>;

  const maxDaily = Math.max(1, ...r.daily.map((d) => d.started));
  const reasonRows = [
    ...Object.entries(r.reasons.validation_error),
    ...Object.entries(r.reasons.submit_result),
  ].filter(([, count]) => count > 0);

  return (
    <div className="grid gap-3">
      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <div className="text-[11px] mb-2" style={{ color: C.muted }}>30 הימים האחרונים · משפך</div>
        <div className="grid gap-1.5">
          {r.stages.map((s) => (
            <div key={s.key}>
              <div className="flex items-center justify-between text-[12px] mb-0.5">
                <span>{FUNNEL_STAGE_LABEL[s.key] || s.key}</span>
                <span style={{ color: C.muted }}>
                  {s.count}
                  {s.dropFromPrevious != null && s.dropFromPrevious > 0
                    ? ` · נשרו ${s.dropFromPrevious}%` : ""}
                </span>
              </div>
              <div className="h-2 rounded-full" style={{ background: C.line }}>
                <div className="h-2 rounded-full" style={{ width: `${s.pct}%`, background: C.teal }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {reasonRows.length > 0 && (
        <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
          <div className="text-[11px] mb-2" style={{ color: C.muted }}>למה נתקעו</div>
          <div className="grid gap-1">
            {reasonRows.map(([reason, count]) => (
              <div key={reason} className="flex items-center justify-between text-[13px]">
                <span>{FUNNEL_REASON_LABEL[reason] || reason}</span>
                <span style={{ color: C.muted }}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <div className="text-[11px] mb-2" style={{ color: C.muted }}>מגמה יומית · התחילו מול נרשמו בהצלחה</div>
        <div className="flex items-end gap-0.5" style={{ height: 60 }}>
          {r.daily.map((d) => (
            <div key={d.date} title={`${d.date}: ${d.started} התחילו, ${d.completed} נרשמו`}
              className="flex-1 flex flex-col justify-end gap-0.5">
              <div style={{ height: `${(d.started / maxDaily) * 100}%`, background: C.line, borderRadius: 2 }} />
              <div style={{ height: `${(d.completed / maxDaily) * 100}%`, background: C.teal, borderRadius: 2, marginTop: -2 }} />
            </div>
          ))}
        </div>
      </div>

      {r.attempts.length > 0 && (
        <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
          <div className="text-[11px] mb-2" style={{ color: C.muted }}>
            מי ניסה להירשם בפועל — למי ששווה לפנות
          </div>
          <div className="grid gap-2">
            {r.attempts.map((a, i) => {
              const ok = a.outcome === "success";
              const tone = ok ? { bg: "#E6F2EC", fg: "#2E7D62" } : { bg: "#FBEDE9", fg: "#7A2E1E" };
              return (
                <div key={i} className="flex items-center justify-between gap-2 text-[13px]">
                  <span dir="ltr" className="truncate">{a.email}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span style={{ color: C.muted }} className="text-[11px]">
                      {new Date(a.at).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <span className="rounded-full px-2 py-0.5 text-[11px]" style={{ background: tone.bg, color: tone.fg }}>
                      {ok ? "נרשם/ה בהצלחה" : FUNNEL_REASON_LABEL[a.outcome] || "לא הושלם"}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
